import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

const api = axios.create({
  baseURL: API,
  withCredentials: true,
});

// Also send Authorization header as fallback for cookies not set (some browsers/preview)
api.interceptors.request.use((config) => {
  const t = localStorage.getItem("lim_token");
  if (t) config.headers.Authorization = `Bearer ${t}`;
  return config;
});

// Completion gate: backend answers 409 TIME_REQUIRED when a task is marked
// complete with no tracked time. A registered handler asks for hours, logs
// them, and the original request is retried transparently.
let timeRequiredHandler = null;
export const registerTimeRequiredHandler = (fn) => { timeRequiredHandler = fn; };

// Short-lived cache for rarely-changing lookups + de-duplication of identical in-flight GETs.
// Any write (POST/PUT/PATCH/DELETE) clears the cache so screens never show stale data after a change.
const CACHE_TTL = { "/users": 60000, "/teams": 300000, "/vaults/finance": 60000, "/vaults/hot_leads": 60000, "/leaves/meta": 60000, "/projects": 30000, "/clients": 60000 };
const cache = new Map();
const inflight = new Map();
let gen = 0;
const rawGet = api.get.bind(api);
api.get = (url, config = {}) => {
  const key = `${url}|${JSON.stringify(config.params || {})}|${config.responseType || ""}`;
  const ttl = CACHE_TTL[url];
  const hit = ttl && cache.get(key);
  if (hit && Date.now() - hit.t < ttl) return Promise.resolve(hit.res);
  if (inflight.has(key)) return inflight.get(key);
  const startGen = gen;
  const p = rawGet(url, config)
    .then((res) => { if (ttl && startGen === gen) cache.set(key, { t: Date.now(), res }); return res; })
    .finally(() => inflight.delete(key));
  inflight.set(key, p);
  return p;
};
api.interceptors.request.use((config) => {
  if (config.method && config.method !== "get" && !String(config.url).endsWith("/batch")) { gen += 1; cache.clear(); inflight.clear(); }
  return config;
});

// Wrap a polling callback so it skips work while the tab is hidden.
export const whenVisible = (fn) => () => { if (!document.hidden) fn(); };
api.interceptors.response.use(undefined, async (error) => {
  const d = error?.response?.data?.detail;
  const cfg = error?.config;
  if (error?.response?.status === 409 && d && d.code === "TIME_REQUIRED" && cfg && timeRequiredHandler) {
    cfg._timeTries = (cfg._timeTries || 0) + 1;
    if (cfg._timeTries <= 10 && (await timeRequiredHandler(d))) return api(cfg);
  }
  return Promise.reject(error);
});

export function formatApiError(detail) {
  if (detail == null) return "Something went wrong.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

export default api;
